import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase.js";

export type AuthState =
  | { status: "loading" }
  | { status: "signed_out" }
  | { status: "needs_enroll"; session: Session }
  | { status: "needs_verify"; session: Session; factorId: string }
  | { status: "ready"; session: Session };

const Ctx = createContext<{ state: AuthState; refresh: () => Promise<void> }>({ state: { status: "loading" }, refresh: async () => {} });

async function resolve(session: Session | null): Promise<AuthState> {
  if (!session) return { status: "signed_out" };
  const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aal?.currentLevel === "aal2") return { status: "ready", session };
  const { data: factors } = await supabase.auth.mfa.listFactors();
  const totp = factors?.totp.find((f) => f.status === "verified");
  if (totp) return { status: "needs_verify", session, factorId: totp.id };
  return { status: "needs_enroll", session };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const refresh = async () => {
    const { data } = await supabase.auth.getSession();
    setState(await resolve(data.session));
  };

  useEffect(() => {
    void refresh();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      // Never await Supabase calls inside this callback (deadlock); defer them.
      setTimeout(() => {
        void resolve(session).then(setState);
      }, 0);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  return <Ctx.Provider value={{ state, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  return useContext(Ctx);
}
